import React, { useState, useRef, useEffect } from 'react';
import { Bars3Icon, BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery, useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import { notificationAPI } from '../../services/api';
import Logo from '../UI/Logo';
import toast from 'react-hot-toast';

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);

  // Fonction pour obtenir le titre basé sur la route actuelle
  const getTitleFromPath = (pathname) => {
    const titles = {
      //'/': user?.role === 'ADMIN' ? 'Tableau de bord administrateur' : 'Mon espace mutualiste',
      '/': 'Tableau de bord',
      '/users': 'Utilisateurs',
      '/admin/subscriptions': 'Souscriptions',
      '/admin/policies': 'Polices',
      '/admin/offers': 'Produits',
      '/admin/parametrage': 'Paramétrage',
      '/admin/parametrage/situation-matrimoniale': 'Paramétrage',
      '/admin/parametrage/liens-parente': 'Paramétrage',
      '/admin/parametrage/fonctions': 'Paramétrage',
      '/admin/parametrage/statuts': 'Paramétrage',
      '/admin/parametrage/entreprises': 'Paramétrage',
      '/admin/parametrage/localites': 'Paramétrage',
      '/admin/parametrage/types-documents': 'Paramétrage',
      '/admin/parametrage/systeme': 'Paramétrage',
      '/offers': 'Nos produits',
      '/subscriptions': 'Mes souscriptions',
      '/policies': 'Mes polices',
      '/contributions': 'Mes cotisations',
      '/beneficiaries': 'Mes bénéficiaires',
      '/payments/self-service': 'Mes paiements',
      '/my-requests': 'Mes demandes',
      '/profile': 'Mon profil'
    };
    
    return titles[pathname] || 'FOPAM+';
  };

  // Récupérer les notifications récentes
  const { data: notificationsData } = useQuery(
    'notifications',
    () => notificationAPI.getNotifications({ limit: 10 }),
    {
      refetchInterval: 30000, // Rafraîchir toutes les 30 secondes
    }
  );

  const notifications = notificationsData?.data?.notifications || [];
  const unreadCount = notificationsData?.data?.unreadCount || 0;

  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Marquer une notification comme lue
  const markAsRead = async (notificationId) => {
    try {
      await notificationAPI.markAsRead([notificationId]);
      // Mise à jour optimiste : marquer la notification comme lue et décrémenter le compteur
      queryClient.setQueryData('notifications', (oldData) => {
        if (!oldData) return oldData;

        const notifications = oldData?.data?.notifications || [];
        const unreadCount = oldData?.data?.unreadCount || 0;

        const updatedNotifications = notifications.map((n) =>
          n.id === notificationId ? { ...n, isRead: true } : n
        );

        const wasUnread = notifications.find((n) => n.id === notificationId && !n.isRead);
        const newUnreadCount = wasUnread ? Math.max(0, unreadCount - 1) : unreadCount;

        return {
          ...oldData,
          data: {
            ...oldData.data,
            notifications: updatedNotifications,
            unreadCount: newUnreadCount
          }
        };
      });
      toast.success('Notification marquée comme lue');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Marquer toutes les notifications comme lues
  const markAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      // Mise à jour optimiste : toutes les notifications deviennent lues et le compteur tombe à 0
      queryClient.setQueryData('notifications', (oldData) => {
        if (!oldData) return oldData;

        const notifications = oldData?.data?.notifications || [];
        const updatedNotifications = notifications.map((n) => ({ ...n, isRead: true }));

        return {
          ...oldData,
          data: {
            ...oldData.data,
            notifications: updatedNotifications,
            unreadCount: 0
          }
        };
      });
      // Fermer la fenêtre contextuelle après avoir tout marqué comme lu
      setShowNotifications(false);
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Formater la date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'À l\'instant';
    } else if (diffInHours < 24) {
      return `Il y a ${Math.floor(diffInHours)}h`;
    } else {
      return date.toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'short' 
      });
    }
  };

  return (
    <div className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo et menu mobile */}
          <div className="flex items-center">
            <button
              type="button"
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              onClick={onMenuClick}
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            
            <div className="lg:hidden ml-4">
              <Logo size="small" />
            </div>
          </div>

          {/* Titre de la page */}
          <div className="hidden lg:block flex-1 text-center px-4">
            <h1 className="text-2xl font-bold text-primary-900 leading-tight">
              {getTitleFromPath(location.pathname)}
            </h1>
          </div>

          {/* Actions utilisateur */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <div className="relative" ref={notificationRef}>
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded-full"
              >
                <BellIcon className="h-6 w-6" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Dropdown des notifications */}
              {showNotifications && (
                <div className="fixed left-2 right-2 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                      <div className="flex items-center space-x-2">
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-xs text-primary-600 hover:text-primary-800 whitespace-nowrap"
                          >
                            Tout marquer comme lu
                          </button>
                        )}
                        <button
                          onClick={() => setShowNotifications(false)}
                          className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">Aucune notification</p>
                      ) : (
                        <div className="space-y-3">
                          {notifications.map((notification) => (
                            <div
                              key={notification.id}
                              className={`p-3 rounded-lg border ${
                                notification.isRead 
                                  ? 'bg-gray-50 border-gray-200' 
                                  : 'bg-blue-50 border-blue-200'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-medium text-gray-900">
                                    {notification.title}
                                  </h4>
                                  <p className="text-sm text-gray-600 mt-1 break-words">
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-2">
                                    {formatDate(notification.createdAt)}
                                  </p>
                                </div>
                                {!notification.isRead && (
                                  <button
                                    onClick={() => markAsRead(notification.id)}
                                    className="flex-shrink-0 text-xs text-primary-600 hover:text-primary-800 whitespace-nowrap"
                                  >
                                    Marquer comme lu
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Menu utilisateur */}
            <div className="relative">
              <div className="flex items-center space-x-3">
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.mutualiste 
                      ? `${user.mutualiste.prenoms} ${user.mutualiste.nom}`
                      : user?.role === 'ADMIN' 
                        ? 'Administrateur'
                        : user?.role === 'AGENT'
                          ? 'Agent'
                        : user?.role === 'SUPERVISEUR'
                          ? 'Superviseur'
                          : user?.email
                    }
                  </p>
                  <p className="text-xs text-gray-500">
                    {user?.mutualiste 
                      ? `${user.mutualiste.matricule}` 
                      : user?.role === 'ADMIN' 
                        ? 'Administrateur' 
                        : user?.role === 'AGENT'
                          ? 'Agent'
                        : user?.role === 'SUPERVISEUR'
                          ? 'Superviseur'
                          : 'Utilisateur'}
                  </p>
                </div>
                
                <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user?.mutualiste 
                      ? `${user.mutualiste.prenoms?.[0] || ''}${user.mutualiste.nom?.[0] || ''}`
                      : user?.role === 'ADMIN' 
                        ? 'AD'
                        : user?.role === 'AGENT'
                          ? 'AG'
                        : user?.role === 'SUPERVISEUR'
                          ? 'SU'
                          : user?.email?.[0]?.toUpperCase() || 'U'
                    }
                  </span>
                </div>
                
                <button
                  onClick={logout}
                  className="text-sm text-gray-500 hover:text-gray-700 ml-2"
                >
                  Déconnexion
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
